import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from 'src/components/loader';
import Alert from 'src/components/ui/Alert';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import routes from 'src/utils/routes';
import { getRouteURI, getShortUuid, getUuid } from 'src/utils/url';

const REMANUFACTURE_REASONS = [
  { value: '', label: 'None' },
  { value: 'part_redesign', label: 'Part Redesign' },
  { value: 'delamination', label: 'Delamination' },
  { value: 'change_parameters', label: 'Change Parameters' },
  { value: 'scan_adjustment', label: 'Scan Adjustment' },
  { value: 'order_print_failure', label: 'Other Print Failure' },
];

const INITIAL_VISIBLE_COUNT = 3;

export const RemanufactureSuccessContent = ({ runUri, run }) => {
  const { closeActionPanel } = useActionPanelStore();

  if (!runUri) return null;

  return (
    <>
      <Alert
        variant="info"
        name="remanufacture-success-direction"
        icon={faInfoCircle}
        iconClassName="mt-1"
        className="d-flex align-items-start gap-10"
      >
        A new run was created for the remanufactured pieces, starting from the printing step.
      </Alert>
      <Link
        to={getRouteURI(routes.run, { uuid: getUuid(runUri) })}
        className="button button--primary button--fill remanufacture-success__open-run"
        onClick={closeActionPanel}
      >
        Open Run {run ? `(${run?.customer_id})` : `(${getShortUuid(runUri)})`}
      </Link>
    </>
  );
};

RemanufactureSuccessContent.propTypes = {
  runUri: PropTypes.string,
  run: PropTypes.shape({
    // eslint-disable-next-line camelcase
    customer_id: PropTypes.string,
  }),
};

RemanufactureSuccessContent.defaultProps = {
  runUri: null,
};

const RemanufactureModal = ({
  onConfirm,
  onDismiss,
  selectedPieces,
}) => {
  const [remanufactureReason, setRemanufactureReason] = useState('');
  const [remanufactureNotes, setRemanufactureNotes] = useState('');
  const [showAllPieces, setShowAllPieces] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { closeActionPanel } = useActionPanelStore();

  const visiblePieces = useMemo(() => {
    if (showAllPieces) {
      return selectedPieces;
    }

    return selectedPieces.slice(0, INITIAL_VISIBLE_COUNT);
  }, [selectedPieces, showAllPieces]);

  const remainingCount =
    selectedPieces.length - INITIAL_VISIBLE_COUNT;

  return (
    <div>
      <div className="mb-3 d-flex flex-wrap gap-5 align-items-center">
        {visiblePieces.map((piece) => (
          <span
            key={piece}
            className="status-badge status-badge--info"
          >
            {getShortUuid(piece)}
          </span>
        ))}

        {remainingCount > 0 && (
          <button
            type="button"
            className="status-badge border-0 bg-transparent status-badge--info text-capitalize"
            onClick={() => setShowAllPieces((prev) => !prev)}
          >
            {showAllPieces
              ? 'Show less'
              : `+ ${remainingCount} more`}
          </button>
        )}
      </div>
      <Alert
        variant="info"
        name="remanufacture-info"
        icon={faInfoCircle}
        iconClassName="mt-1"
        className="d-flex align-items-start gap-10"
      >
        The selected pieces will be completely remanufactured from the printing step.
        If you only wish to redo this production run, please complete this action in Flows rather than the QR app.
        Selected pieces will be marked as incomplete. New piece IDs will be
        generated and scheduled into the next available run.
      </Alert>

      <div>
        <label>Reason</label>
        <select
          className="form-control mb-4"
          value={remanufactureReason}
          onChange={(event) => setRemanufactureReason(event.target.value)}
        >
          {REMANUFACTURE_REASONS.map((reason) => (
            <option key={reason.value} value={reason.value}>
              {reason.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label>Notes (optional)</label>
        <textarea
          className="form-control mb-4"
          value={remanufactureNotes}
          onChange={(event) => setRemanufactureNotes(event.target.value)}
        />
      </div>
      <div className="button-group button-group--panel">
        <button
          type="button"
          className="button"
          disabled={isSubmitting}
          onClick={() => {
            onDismiss();
            closeActionPanel();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={isSubmitting}
          onClick={async () => {
            try {
              setIsSubmitting(true);

              await onConfirm({
                reason: remanufactureReason,
                notes: remanufactureNotes,
              });
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {!isSubmitting ? 'Confirm Remanufacture' : <Loader inline />}
        </button>
      </div>
    </div>
  );
};

RemanufactureModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  selectedPieces: PropTypes.array.isRequired,
};

export default RemanufactureModal;
